import { useEffect } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { cn } from "@/shared/lib/cn";
import { useSourcesPanel } from "../hooks/useSourcesPanel";
import { faviconUrl, sourceHost, sourceLabel } from "../lib/sources";
import type { Source } from "../lib/sseEvents";

/** Sliding right-hand panel listing every source cited in a message. */
export function SourcesPanel() {
  const { t } = useTranslation();
  const { sources, isOpen, focusUrl, close } = useSourcesPanel();

  useEffect(() => {
    if (!isOpen) return;
    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") close();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isOpen, close]);

  return createPortal(
    <aside
      aria-label={t("chat.thread.sources")}
      aria-hidden={!isOpen}
      className={cn(
        "fixed inset-y-0 right-0 z-40 flex w-[380px] max-w-[88vw] flex-col bg-panel-2 shadow-[var(--shadow-pill)] transition-transform duration-300 ease-house",
        isOpen ? "translate-x-0" : "pointer-events-none translate-x-full",
      )}
    >
      <header className="flex h-[60px] flex-none items-center justify-between pl-8 pr-4">
        <span className="font-sans text-[14px] font-semibold text-ink">
          {t("chat.thread.sources")}
        </span>
        <button
          type="button"
          aria-label={t("common.close")}
          onClick={close}
          className="grid size-8 place-items-center rounded-[8px] text-ink outline-none transition-colors duration-150 ease-house hover:bg-line-soft hover:text-ink focus-visible:bg-line-soft"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" className="size-4" aria-hidden="true">
            <path d="M18 6 6 18M6 6l12 12" />
          </svg>
        </button>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
        <ul className="flex flex-col gap-1.5">
          {sources.map((source, i) => (
            <li key={`${source.url}-${i}`}>
              <SourceCard source={source} focused={source.url === focusUrl} />
            </li>
          ))}
        </ul>
      </div>
    </aside>,
    document.body,
  );
}

function SourceCard({ source, focused }: { source: Source; focused: boolean }) {
  const host = sourceHost(source.url);

  return (
    <a
      href={source.url}
      target="_blank"
      rel="noreferrer noopener"
      className={cn(
        "block rounded-md bg-bubble px-4 py-3 outline-none transition-colors duration-150 ease-house hover:bg-tint focus-visible:bg-tint",
        focused && "ring-1 ring-ink",
      )}
    >
      <span className="flex items-center gap-2">
        {host ? (
          <img
            src={faviconUrl(host)}
            alt=""
            aria-hidden="true"
            loading="lazy"
            width={16}
            height={16}
            className="size-4 shrink-0 rounded-[3px]"
          />
        ) : null}
        <span className="truncate font-sans text-[12px] text-ink-soft">{sourceLabel(source)}</span>
      </span>
      {source.title ? (
        <span className="mt-1.5 block font-sans text-[13.5px] font-medium leading-snug text-ink [display:-webkit-box] [-webkit-box-orient:vertical] [-webkit-line-clamp:2] overflow-hidden">
          {source.title}
        </span>
      ) : null}
      {source.content ? (
        <span className="mt-1 block font-sans text-[12.5px] leading-snug text-ink-faint [display:-webkit-box] [-webkit-box-orient:vertical] [-webkit-line-clamp:3] overflow-hidden">
          {source.content}
        </span>
      ) : null}
    </a>
  );
}
